import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./MainspringPaywall.module.scss"

/**
 * MainspringPaywall (`/mainspring`) — a self-contained EXPIRATION / RENEWAL
 * paywall for Private Photo Vault, staged as a WARM WOOD-AND-BRASS WIND-UP MUSIC
 * BOX. The user's year of memories is the photo CYLINDER inside the box; the
 * vault is the mechanism that keeps it turning. A mainspring is what stores one
 * wind and quietly runs the whole thing — the perfect honest metaphor for a
 * subscription that runs for a term and is renewed by winding it again.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/mainspring.md
 *
 * Mechanic (new to the ledger): ROTARY KEY-WIND. Turn the brass key (drag it —
 * any circular/cranking motion winds it, forgivingly) and the mainspring coils
 * tight, the photo cylinder turns and the box comes to life. Once fully wound it
 * plays on its own — the cylinder keeps gently turning. "Wind it" and the
 * keyboard (Enter/Space, a part-wind per press) also wind it; a short auto-demo
 * gives it a couple of turns then hands the key over. The photos stay full
 * colour throughout — winding is warmth + motion, not a grey→colour reveal.
 *
 * The yearly argument is native to the object and honest: ONE FULL WIND PLAYS
 * ALL YEAR — Monthly means coming back to re-wind it every single month. Wind
 * fatigue is the monthly hassle made visible; the plate reads the real coverage
 * date (today + the plan's term).
 *
 * Every ledger axis is chosen to differ from the 9 prior expiration concepts:
 *   - Layout:      a CUTAWAY WIND-UP MUSIC BOX (barrel + mainspring coil + photo
 *                  cylinder + comb + protruding key) — not Deep Freeze's hero
 *                  card, Unbroken's month grid, Ahead's road, Keystone's arch,
 *                  Keep Posted's flap board, Patchwork's quilt, Evergreen's
 *                  radial wheel, Keepsake's card deck or Beacon's harbour.
 *   - Interaction: a ROTARY KEY-WIND that accumulates + a self-playing run once
 *                  wound — distinct from every scrub / hold / flip / toss / paint /
 *                  dial / riffle / node-graph / draw-a-link / lever-pull / stitch /
 *                  lasso / drag-a-piece / rope-haul prior.
 *   - Register:    gently-MAGICAL, satisfying mechanical CRAFT — the tactile
 *                  pleasure of winding a fine mechanism and hearing it come alive.
 *                  Not Deep Freeze cold-fear, Unbroken earned-pride, Ahead hopeful-
 *                  road, Keystone load-bearing, Keep Posted retro-clacky, Patchwork
 *                  handmade-textile, Evergreen nurturing-seasonal, Keepsake collector
 *                  or Beacon steadfast-vigil.
 *   - Art:        LIGHT warm walnut + polished brass + PHOTOGRAPHIC memory cylinder
 *                  — brand blue = the WOUND / running / protected state (tight
 *                  spring, wound plate, featured yearly), warm amber/brass = the
 *                  slack / winding-down state + the key. Photos always full colour.
 *   - Value:      CONTINUITY of automatic protection for RENEWAL via a wind-up
 *                  "keep it running" frame + the honest ONE-WIND-A-YEAR vs
 *                  RE-WIND-MONTHLY convenience contrast.
 *
 * Honest & App Store compliant: the mainspring only keeps the music playing — it
 * never holds photos hostage and nothing is ever deleted (copy says so). The
 * coverage date is the real today + term, computed live; Save % is exact
 * yearly-vs-monthly arithmetic; no fake countdown or scarcity. Purchase is never
 * gated on the demo. Yearly is the featured, default plan and carries the most
 * prominent price; monthly is small and secondary. Memory tiles are bundled demo
 * photos from `public/byheart/` (no network egress).
 */

type PlanKey = "yearly" | "monthly"

// this year's memories, one tile per month, riding the cylinder (bundled photos)
const MONTHS = [
  "m01.jpg",
  "m02.jpg",
  "m03.jpg",
  "m04.jpg",
  "m05.jpg",
  "m06.jpg",
  "m07.jpg",
  "m08.jpg",
  "m09.jpg",
  "m10.jpg",
  "m11.jpg",
  "m12.jpg",
].map((f) => `/byheart/${f}`)

// cylinder geometry — must match the .scss tile sizing
const TILE = 58
const GAP = 8
const PITCH = TILE + GAP
const SETW = MONTHS.length * PITCH // one full turn of the cylinder, in px

const FULL_WIND = 820 // px of cranking that fully winds the mainspring
const PLAY_SPEED = 0.34 // px/frame the cylinder turns on its own once wound
const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

// ── tiny inline icons ─────────────────────────────────────────────────────────
const Tick = ({ size = 11 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const Lock = ({ size = 13 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <rect
      x={4.5}
      y={10.5}
      width={15}
      height={10}
      rx={2.6}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
    />
    <path
      d="M7.5 10.5V8a4.5 4.5 0 0 1 9 0v2.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
    />
  </svg>
)
const Note = ({ size = 13 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M9 17.5V6.2l9-1.8v9.4"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx={6.4} cy={17.6} r={2.6} fill="currentColor" />
    <circle cx={15.4} cy={15.6} r={2.6} fill="currentColor" />
  </svg>
)

// build the mainspring's coil as a smooth Archimedean spiral path (deterministic)
function spiralPath(cx: number, cy: number, turns: number, rMax: number) {
  const steps = Math.round(turns * 40)
  const b = rMax / (turns * 2 * Math.PI)
  let d = ""
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * turns * 2 * Math.PI
    const r = b * t
    const x = cx + r * Math.cos(t)
    const y = cy + r * Math.sin(t)
    d += `${i === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)} `
  }
  return d.trim()
}

const MainspringPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  // ── pricing (real debug data; yearly featured) ──────────────────────────────
  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // the real coverage date if you wind/renew now — today + the plan's term
  const coverThrough = useMemo(() => {
    const d = new Date()
    d.setMonth(d.getMonth() + (plan === "yearly" ? 12 : 1))
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
  }, [plan])

  // ── mechanism state ──────────────────────────────────────────────────────────
  const [charge, setCharge] = useState(0) // 0 (slack) → 1 (fully wound)
  const [spin, setSpin] = useState(0) // px the cylinder/key has turned
  const [handed, setHanded] = useState(false) // auto-demo done / user has the key

  const woundRef = useRef(0) // px of manual cranking accumulated
  const draggingRef = useRef(false)
  const lastRef = useRef({ x: 0, y: 0 })
  const rampRef = useRef<number | null>(null)
  const demoRef = useRef<number | null>(null)
  const playRef = useRef<number | null>(null)

  const lit = charge > 0.995

  const spiral = useMemo(() => spiralPath(50, 50, 4.4, 41), [])

  const stopRamp = () => {
    if (rampRef.current) cancelAnimationFrame(rampRef.current)
    rampRef.current = null
  }
  const stopDemo = () => {
    if (demoRef.current) window.clearTimeout(demoRef.current)
    demoRef.current = null
  }

  // ramp the wind to a target (used by the demo, "Wind it" + keyboard)
  const rampWoundTo = useCallback((targetWound: number, ms: number) => {
    stopRamp()
    const from = woundRef.current
    let start: number | null = null
    const tick = (t: number) => {
      if (start === null) start = t
      const k = clamp((t - start) / ms, 0, 1)
      const eased = 1 - Math.pow(1 - k, 3)
      const nextWound = from + (targetWound - from) * eased
      const delta = nextWound - woundRef.current
      woundRef.current = nextWound
      setCharge(clamp(nextWound / FULL_WIND, 0, 1))
      setSpin((s) => s + delta)
      if (k < 1) rampRef.current = requestAnimationFrame(tick)
      else rampRef.current = null
    }
    rampRef.current = requestAnimationFrame(tick)
  }, [])

  // ── the box plays once wound — the cylinder keeps gently turning ─────────────
  useEffect(() => {
    if (reduceMotion) return
    const loop = () => {
      if (charge > 0.995 && !draggingRef.current) {
        setSpin((s) => s + PLAY_SPEED)
      }
      playRef.current = requestAnimationFrame(loop)
    }
    playRef.current = requestAnimationFrame(loop)
    return () => {
      if (playRef.current) cancelAnimationFrame(playRef.current)
      playRef.current = null
    }
  }, [charge, reduceMotion])

  // ── the teaching wind: a couple of turns, then hand the key over ─────────────
  useEffect(() => {
    if (reduceMotion) {
      woundRef.current = FULL_WIND
      setCharge(1)
      setSpin(SETW * 0.5)
      setHanded(true)
      return
    }
    demoRef.current = window.setTimeout(() => {
      rampWoundTo(FULL_WIND * 0.4, 1150)
      demoRef.current = window.setTimeout(() => setHanded(true), 1300)
    }, 700)
    return () => {
      stopDemo()
      stopRamp()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // ── pointer: crank the key (any circular / dragging motion winds it) ─────────
  const beginInteract = () => {
    stopDemo()
    stopRamp()
    setHanded(true)
  }
  const onDown = (e: React.PointerEvent) => {
    beginInteract()
    draggingRef.current = true
    lastRef.current = { x: e.clientX, y: e.clientY }
    ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
  }
  const onMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    const dx = e.clientX - lastRef.current.x
    const dy = e.clientY - lastRef.current.y
    lastRef.current = { x: e.clientX, y: e.clientY }
    const d = Math.hypot(dx, dy)
    if (d <= 0) return
    const before = woundRef.current
    woundRef.current = clamp(woundRef.current + d, 0, FULL_WIND)
    const gained = woundRef.current - before
    setCharge(clamp(woundRef.current / FULL_WIND, 0, 1))
    setSpin((s) => s + gained)
    if (before < FULL_WIND && woundRef.current >= FULL_WIND) {
      native.logMixpanelEvent("html_mainspring_wound", {})
    }
  }
  const onUp = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    ;(e.currentTarget as Element).releasePointerCapture?.(e.pointerId)
  }

  const windIt = () => {
    beginInteract()
    rampWoundTo(FULL_WIND, 900)
    native.logMixpanelEvent("html_mainspring_wind_button", {})
  }
  const letDown = () => {
    beginInteract()
    rampWoundTo(0, 700)
    native.logMixpanelEvent("html_mainspring_reset", {})
  }
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      beginInteract()
      rampWoundTo(clamp(woundRef.current + FULL_WIND * 0.34, 0, FULL_WIND), 480)
    }
  }

  // ── plan + purchase wiring ────────────────────────────────────────────────────
  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  // ── copy ───────────────────────────────────────────────────────────────────
  const pct = Math.round(charge * 100)
  const headline = lit
    ? "One wind. A whole year kept."
    : "Keep the memories playing."
  const sub = lit
    ? "Full-quality originals, private and encrypted, backed up and in sync on every device — kept turning automatically, all year long."
    : "Your vault has quietly kept every photo and video you saved this year. Give the key a wind — and keep it running, automatically, for the year ahead."
  const hint = !handed
    ? "Winding it up…"
    : lit
      ? "Fully wound — this is the year Premium keeps running for you. Yearly winds it once; Monthly, you come back and re-wind every month."
      : charge > 0.02
        ? `${pct}% wound — keep turning the key.`
        : "Turn the brass key — wind the mainspring and bring your memories to life."

  const ctaLabel =
    plan === "yearly"
      ? hasTrial
        ? `Start ${trialDays} days free`
        : "Wind it for the year — Yearly"
      : "Choose monthly"
  const ctaSub =
    plan === "yearly"
      ? hasTrial
        ? `${trialDays} days free, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · auto-renews · cancel anytime`
      : `${monthlyPrice}/mo · auto-renews · cancel anytime`

  // cylinder offset wraps every full turn; key rotation follows the same spin
  const roll = -(((spin % SETW) + SETW) % SETW)
  const keyRot = spin * 0.5

  const stageVars = {
    ["--charge" as any]: charge.toFixed(3),
    ["--roll" as any]: `${roll.toFixed(1)}px`,
    ["--keyrot" as any]: `${keyRot.toFixed(1)}deg`,
  } as React.CSSProperties

  return (
    <div className={styles.page}>
      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.brandKey} aria-hidden>
            <Lock size={12} />
          </span>
          <span>Private Photo Vault</span>
          <span className={styles.premiumPip}>Premium</span>
        </div>
        <h1 className={`${styles.title} ${lit ? styles.titleLit : ""}`}>
          {headline}
        </h1>
        <p className={styles.sub}>{sub}</p>
      </header>

      {/* ── the wind-up music box (cutaway) ───────────────────────────────────── */}
      <div
        className={`${styles.stage} ${lit ? styles.stageLit : ""} ${
          reduceMotion ? styles.stageStill : ""
        }`}
        style={stageVars}
        role="group"
        tabIndex={0}
        aria-label="A wind-up music box of your memories — turn the key to wind it"
        onKeyDown={onKeyDown}
      >
        <span className={styles.grain} aria-hidden />

        {/* the barrel + mainspring coil that stores the wind */}
        <div className={styles.barrel} aria-hidden>
          <svg className={styles.coil} viewBox="0 0 100 100">
            <circle className={styles.coilRing} cx="50" cy="50" r="44" />
            <path className={styles.coilTrack} d={spiral} />
            <path className={styles.coilLive} d={spiral} pathLength={1} />
            <circle className={styles.arbor} cx="50" cy="50" r="6.5" />
          </svg>
          <span className={styles.barrelLabel}>MAINSPRING</span>
        </div>

        {/* the photo cylinder — this year's memories, turning */}
        <div className={styles.window}>
          <div className={styles.cylinder}>
            <div className={styles.track}>
              {[...MONTHS, ...MONTHS].map((src, i) => (
                <span className={styles.pane} key={i}>
                  <img
                    src={src}
                    alt=""
                    draggable={false}
                    onError={(e) =>
                      ((e.currentTarget as HTMLImageElement).style.opacity =
                        "0")
                    }
                  />
                </span>
              ))}
            </div>
            <span className={styles.curve} aria-hidden />
            <span className={styles.gloss} aria-hidden />
          </div>
          {/* the comb the cylinder's pins pluck */}
          <span className={styles.comb} aria-hidden>
            {Array.from({ length: 16 }).map((_, i) => (
              <span key={i} style={{ ["--i" as any]: i }} />
            ))}
          </span>
          {/* little notes lifting off as it plays */}
          <span className={styles.notes} aria-hidden>
            <i className={styles.note1}>
              <Note size={12} />
            </i>
            <i className={styles.note2}>
              <Note size={10} />
            </i>
            <i className={styles.note3}>
              <Note size={14} />
            </i>
          </span>
        </div>

        {/* the brass wind key — crank it round */}
        <span
          className={styles.key}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          role="button"
          aria-label="Turn the key to wind the mainspring"
        >
          <span className={styles.keyShaft} aria-hidden />
          <span className={styles.keyHead} aria-hidden>
            <svg viewBox="0 0 100 100">
              <g className={styles.keyWings}>
                <rect x="2" y="37" width="40" height="26" rx="13" />
                <rect x="58" y="37" width="40" height="26" rx="13" />
                <rect x="37" y="2" width="26" height="40" rx="13" />
                <rect x="37" y="58" width="26" height="40" rx="13" />
                <circle cx="50" cy="50" r="15" />
                <circle className={styles.keyHub} cx="50" cy="50" r="5.5" />
              </g>
            </svg>
          </span>
          {!handed ? null : charge < 0.02 ? (
            <span className={styles.keyHalo} aria-hidden />
          ) : null}
        </span>

        {/* front fittings: status tag + coverage plate */}
        <span className={styles.woundTag} aria-hidden>
          <span className={styles.woundDot} />
          {lit ? "WOUND · PLAYING" : `WOUND ${pct}%`}
        </span>
        <span className={styles.plate} aria-hidden>
          <span className={styles.plateKey}>
            <Note size={10} />
          </span>
          PLAYS THROUGH {coverThrough.toUpperCase()}
        </span>
      </div>

      <p
        className={`${styles.hintLine} ${lit ? styles.hintLit : ""}`}
        aria-live="polite"
      >
        {hint}
      </p>

      <div className={styles.tools}>
        <button className={styles.toolPrimary} onClick={windIt} disabled={lit}>
          <Note size={13} />
          {lit ? "Wound & playing" : "Wind it"}
        </button>
        <button
          className={styles.toolGhost}
          onClick={letDown}
          disabled={charge <= 0.001}
        >
          Let it wind down
        </button>
      </div>

      {/* what keeps running when it's wound — the honest Premium bundle */}
      <ul className={styles.keeps}>
        {[
          "Every original kept in full quality — backed up automatically",
          "Private & encrypted — only your passcode opens the vault",
          "In sync on every device — your whole library, always turning",
        ].map((t) => (
          <li key={t}>
            <span className={styles.keepDot} aria-hidden>
              <Tick size={10} />
            </span>
            {t}
          </li>
        ))}
      </ul>

      {/* ── plans — yearly featured, monthly small & secondary ─────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${
            plan === "yearly" ? styles.planSel : ""
          }`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.planMain}>
            <span className={styles.planName}>Yearly</span>
            <span className={styles.planNote}>One wind — plays all year</span>
          </span>
          <span className={styles.planPrice}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/yr</span>
            <span className={styles.planSmall}>
              {yearlyPerMonth}/mo
              {savingsPct > 0 ? ` · save ${savingsPct}%` : ""}
            </span>
          </span>
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
          )}
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.planMonthSel : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthNote}>Re-wind every month</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>{ctaLabel}</span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        The mainspring only keeps the music playing — it never holds your
        memories hostage. Your photos are always yours, and nothing is ever
        deleted.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default MainspringPaywall
